import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import api from '../api';
import { getAuthContext } from '../utils/roleAccess';

const WhatsappUnreadContext = createContext({
  count: 0,
  refresh: () => {},
});

export function WhatsappUnreadBadge({ count, sx }) {
  const value = Number(count) || 0;
  if (value < 1) return null;

  return (
    <Box
      component="span"
      sx={{
        ml: 1,
        minWidth: 18,
        height: 18,
        px: 0.5,
        borderRadius: 9,
        bgcolor: 'error.main',
        color: '#fff',
        fontSize: 11,
        fontWeight: 800,
        lineHeight: '18px',
        textAlign: 'center',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...sx,
      }}
    >
      {value > 99 ? '99+' : value}
    </Box>
  );
}

export function WhatsappUnreadProvider({ children }) {
  const [count, setCount] = useState(0);
  const { isAdmin, isSuperAdmin, isManager } = getAuthContext();
  const allowed = isAdmin || isSuperAdmin || isManager;

  const refresh = useCallback(async () => {
    if (!allowed) {
      setCount(0);
      return;
    }
    try {
      const response = await api.get('/api/v1/whatsapp_inbound_messages/unread');
      setCount(Number(response.data?.unreadConversations) || 0);
    } catch (_error) {
      setCount(0);
    }
  }, [allowed]);

  useEffect(() => {
    if (!allowed) {
      setCount(0);
      return undefined;
    }

    const tick = () => {
      if (document.hidden) return;
      if (window.location.pathname.includes('whatsapp-inbox')) return;
      refresh();
    };

    refresh();
    const timer = setInterval(tick, 45000);
    const onVisibility = () => {
      if (!document.hidden) tick();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [allowed, refresh]);

  return (
    <WhatsappUnreadContext.Provider value={{ count, refresh, setCount }}>
      {children}
    </WhatsappUnreadContext.Provider>
  );
}

export const useWhatsappUnread = () => useContext(WhatsappUnreadContext);
